
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function About() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'Hakkımızda - GüvenTar Sistemleri | Tarımsal Sulama Uzmanları Antalya';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'GüvenTar Sistemleri hakkında bilgi edinin. 2005\'ten beri tarımsal sulama sistemlerinde uzman ekibimiz, vizyonumuz, misyonumuz ve değerlerimiz. Antalya Serik merkezli 25+ yıl deneyim.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'GüvenTar hakkında, tarımsal sulama uzmanları, firma hikayesi, vizyon misyon, Antalya sulama firması');
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://www.guventar.com'}/hakkimizda`);
    }

    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Hakkımızda - GüvenTar Sistemleri",
      "description": "GüvenTar Sistemleri hakkında bilgi edinin. 2005'ten beri tarımsal sulama sistemlerinde uzman ekibimiz, vizyonumuz, misyonumuz ve değerlerimiz.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://www.guventar.com'}/hakkimizda`,
      "mainEntity": {
        "@type": "Organization",
        "name": "GüvenTar Sistemleri",
        "foundingDate": "2005",
        "description": "Tarımsal sulama sistemlerinde uzman firma",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Cumalı Mh. Antalya Cd. No:9",
          "addressLocality": "Serik",
          "addressRegion": "Antalya",
          "addressCountry": "TR"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'tr' ? 'en' : 'tr';
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
  };

  const values = [
    {
      icon: 'ri-eye-line',
      title: language === 'tr' ? 'Vizyonumuz' : t('about.vision.vision.title'),
      description: language === 'tr' ? 'Türkiye\'de tarımsal sulama teknolojilerinde öncü firma olmak ve sürdürülebilir tarım uygulamalarını yaygınlaştırmak.' : t('about.vision.vision.desc')
    },
    {
      icon: 'ri-target-line',
      title: language === 'tr' ? 'Misyonumuz' : t('about.vision.mission.title'),
      description: language === 'tr' ? 'Tarımsal üretimde su verimliliğini artırarak çiftçilerimizin karlılığını yükseltmek ve doğal kaynakları korumak.' : t('about.vision.mission.desc')
    }
  ];

  const coreValues = [
    {
      icon: 'ri-shield-check-line',
      title: language === 'tr' ? 'Güven' : t('about.coreValues.trust.title'),
      description: language === 'tr' ? 'Müşterilerimizle kurduğumuz güven ilişkisi, işimizin temel taşıdır. Sözümüzü tutmak ve kaliteli hizmet sunmak önceliğimizdir.' : t('about.coreValues.trust.desc')
    },
    {
      icon: 'ri-award-line',
      title: language === 'tr' ? 'Kalite' : t('about.coreValues.quality.title'),
      description: language === 'tr' ? 'Kullandığımız malzemelerden sunduğumuz hizmete kadar her aşamada en yüksek kalite standartlarını uygularız.' : t('about.coreValues.quality.desc')
    },
    {
      icon: 'ri-customer-service-2-line',
      title: language === 'tr' ? 'Müşteri Memnuniyeti' : t('about.coreValues.satisfaction.title'),
      description: language === 'tr' ? 'Müşterilerimizin ihtiyaçlarını anlamak ve beklentilerini aşan çözümler sunmak temel yaklaşımımızdır.' : t('about.coreValues.satisfaction.desc')
    },
    {
      icon: 'ri-leaf-line',
      title: language === 'tr' ? 'Doğa Dostu' : t('about.coreValues.nature.title'),
      description: language === 'tr' ? 'Çevre dostu teknolojiler kullanarak doğal kaynakları korumak ve gelecek nesillere yaşanabilir bir dünya bırakmak sorumluluğumuzdur.' : t('about.coreValues.nature.desc')
    }
  ];

  const timeline = [
    { year: '2005', title: language === 'tr' ? 'Kuruluş' : t('about.timeline.items.2005.title'), description: language === 'tr' ? 'GüvenTar Sistemleri kuruldu' : t('about.timeline.items.2005.desc') },
    { year: '2007', title: language === 'tr' ? 'İlk 50 Proje' : t('about.timeline.items.2007.title'), description: language === 'tr' ? 'Başarıyla tamamlanan 50. proje' : t('about.timeline.items.2007.desc') },
    { year: '2010', title: language === 'tr' ? 'İlk 100 Proje' : t('about.timeline.items.2010.title'), description: language === 'tr' ? 'Başarıyla tamamlanan 100. proje' : t('about.timeline.items.2010.desc') },
    { year: '2015', title: language === 'tr' ? 'Teknoloji Yatırımı' : t('about.timeline.items.2015.title'), description: language === 'tr' ? 'Modern otomasyon sistemleri' : t('about.timeline.items.2015.desc') },
    { year: '2020', title: language === 'tr' ? 'Bölgesel Genişleme' : t('about.timeline.items.2020.title'), description: language === 'tr' ? 'Türkiye genelinde hizmet' : t('about.timeline.items.2020.desc') },
    { year: '2024', title: language === 'tr' ? 'Sürdürülebilirlik' : t('about.timeline.items.2024.title'), description: language === 'tr' ? '500+ tamamlanan proje' : t('about.timeline.items.2024.desc') }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        <h1 style={{ position: 'absolute', left: '-9999px' }}>Hakkımızda - GüvenTar Sistemleri</h1>
        
        {/* Language Toggle Button - Hidden on Mobile */}
        <div className="fixed top-4 sm:top-6 right-4 sm:right-6 z-50 hidden sm:block">
          <button
            onClick={toggleLanguage}
            className="group relative flex items-center space-x-2 sm:space-x-3 bg-white/90 backdrop-blur-md text-gray-700 px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-gray-200/50 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-800 to-green-900 rounded-full flex items-center justify-center shadow-sm">
              <i className="ri-global-line text-white text-sm sm:text-lg"></i>
            </div>
            <div className="hidden sm:flex flex-col items-start">
              <span className="text-xs text-gray-500 font-medium">{t('language.label')}</span>
              <span className="text-sm font-semibold text-gray-800">
                {t('language.switch')}
              </span>
            </div>
            <span className="sm:hidden text-sm font-semibold text-gray-800">
              {language === 'tr' ? 'EN' : 'TR'}
            </span>
            <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-gray-600 transition-colors hidden sm:block"></i>
          </button>
        </div>

        {/* Phone Call Button */}
        <a
          href="tel:+905534145263"
          className="fixed right-6 bottom-24 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
          title="Hemen Ara"
        >
          <i className="ri-phone-line text-xl"></i>
        </a>

        {/* WhatsApp Button */}
        <a
          href="https://wa.me/905534145263"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed right-6 bottom-6 w-14 h-14 bg-green-800 hover:bg-green-900 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
          title="WhatsApp"
        >
          <i className="ri-whatsapp-line text-xl"></i>
        </a>
        
        {/* Main Content with Left Margin for Desktop */}
        <div className="lg:ml-80">
          {/* Hero Section */}
          <section 
            className="relative h-96 flex items-center justify-center bg-cover bg-center"
            style={{
              backgroundImage: `linear-gradient(rgba(27, 94, 32, 0.8), rgba(27, 94, 32, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20agricultural%20engineering%20team%20working%20in%20modern%20office%20with%20irrigation%20system%20blueprints%20and%20technical%20drawings%2C%20sustainable%20farming%20technology%20planning%2C%20green%20agricultural%20landscape%20background&width=1920&height=600&seq=about-hero&orientation=landscape')`
            }}
          >
            <div className="text-center text-white px-4 max-w-4xl mx-auto">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('nav.about')}
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl font-light">
                {language === 'tr' ? 'Tarımsal sulama sistemlerinde güvenilir çözüm ortağınız' : t('about.hero.subtitle')}
              </p>
            </div>
          </section>

          {/* Company Story */}
          <section className="py-12 sm:py-16 lg:py-20 bg-white/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 lg:mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {language === 'tr' ? 'Firma Hikayemiz' : t('about.story.title')}
                  </h2>
                  <div className="space-y-6 lg:space-y-8 text-gray-600 leading-relaxed text-base lg:text-lg">
                    <p>
                      <strong className="text-green-800 text-lg lg:text-xl">GüvenTar Sistemleri</strong>, {language === 'tr' ? '2005 yılında tarımda verimli su yönetimi ve sürdürülebilir üretim amacıyla kurulmuş yenilikçi bir mühendislik firmasıdır. Kuruluşumuzdan bu yana, Türkiye\'nin çeşitli bölgelerinde yüzlerce başarılı proje gerçekleştirdik.' : t('about.story.content1')}
                    </p>
                    <p>
                      {language === 'tr' ? '25 yılı aşkın tecrübemizle, damla sulama sistemlerinden modern otomasyon çözümlerine kadar geniş bir yelpazede hizmet sunuyoruz. Müşterilerimizin ihtiyaçlarına özel çözümler geliştirerek, tarımsal üretimde verimliliği artırmayı hedefliyoruz.' : t('about.story.content2')}
                    </p>
                    <p>
                      {language === 'tr' ? 'Teknolojik gelişmeleri yakından takip eden ekibimiz, çevre dostu ve sürdürülebilir tarım uygulamalarını destekleyerek, doğal kaynakların korunmasına katkıda bulunmaktadır.' : t('about.story.content3')}
                    </p>
                  </div>
                </div>
                <div className="relative mt-8 lg:mt-0">
                  <div className="bg-white/60 backdrop-blur-sm p-6 lg:p-8 rounded-3xl shadow-xl border border-white/20">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Modern%20agricultural%20irrigation%20company%20office%20building%20with%20green%20landscaping%2C%20professional%20engineering%20firm%20headquarters%2C%20sustainable%20architecture%20with%20glass%20facade%20and%20green%20elements%2C%20corporate%20building%20design&width=600&height=400&seq=company-building&orientation=landscape"
                      alt="GüvenTar Sistemleri Ofisi"
                      className="w-full h-60 sm:h-72 lg:h-80 object-cover object-top rounded-2xl shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="py-12 sm:py-16 lg:py-20 bg-gray-50/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {language === 'tr' ? 'Tarihçemiz' : t('about.timeline.title')}
                </h2>
                <p className="text-lg lg:text-xl text-gray-600">
                  {language === 'tr' ? '20 yıllık yolculuğumuzda önemli kilometre taşları' : t('about.timeline.subtitle')}
                </p>
              </div>

              <div className="relative">
                {/* Desktop Timeline */}
                <div className="hidden lg:block">
                  <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-green-800"></div>
                  <div className="space-y-12">
                    {timeline.map((item, index) => (
                      <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                        <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                          <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20">
                            <div className="text-3xl font-bold text-green-800 mb-2">{item.year}</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                            <p className="text-gray-600">{item.description}</p>
                          </div>
                        </div>
                        <div className="relative z-10">
                          <div className="w-6 h-6 bg-green-800 rounded-full border-4 border-white shadow-lg"></div>
                        </div>
                        <div className="w-1/2"></div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile Timeline */}
                <div className="lg:hidden">
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-green-800"></div>
                  <div className="space-y-8">
                    {timeline.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="relative z-10 mr-6">
                          <div className="w-4 h-4 bg-green-800 rounded-full border-2 border-white shadow-lg"></div>
                        </div>
                        <div className="flex-1">
                          <div className="bg-white/60 backdrop-blur-sm p-4 sm:p-6 rounded-2xl shadow-lg border border-white/20">
                            <div className="text-2xl font-bold text-green-800 mb-2">{item.year}</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                            <p className="text-gray-600 text-sm sm:text-base">{item.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Vision & Mission */}
          <section className="py-12 sm:py-16 lg:py-20 bg-white/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {language === 'tr' ? 'Vizyon & Misyon' : t('about.vision.title')}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                {values.map((item, index) => (
                  <div key={index} className="bg-white/60 backdrop-blur-sm p-6 sm:p-8 lg:p-10 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20">
                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-green-800 to-green-900 rounded-2xl flex items-center justify-center mb-6 lg:mb-8 shadow-lg">
                      <i className={`${item.icon} text-3xl lg:text-4xl text-white`}></i>
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-semibold mb-4 lg:mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-base lg:text-lg">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-12 sm:py-16 lg:py-20 bg-gray-50/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {language === 'tr' ? 'Değerlerimiz' : t('about.coreValues.title')}
                </h2>
                <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
                  {language === 'tr' ? 'İş yapış şeklimizi belirleyen temel değerlerimiz, müşterilerimizle kurduğumuz güçlü ilişkilerin temelidir.' : t('about.coreValues.subtitle')}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {coreValues.map((value, index) => (
                  <div key={index} className="flex items-start space-x-4 sm:space-x-6 p-6 lg:p-8 bg-white/60 backdrop-blur-sm rounded-2xl hover:bg-white/80 transition-all duration-300 shadow-lg border border-white/20">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-green-800 to-green-900 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                      <i className={`${value.icon} text-lg sm:text-xl lg:text-2xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-3 lg:mb-4 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-sm sm:text-base lg:text-lg">
                        {value.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Team Section */}
          <section className="py-12 sm:py-16 lg:py-20 bg-white/80 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 lg:mb-16">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {language === 'tr' ? 'Uzman Ekibimiz' : t('about.team.title')}
                </h2>
                <p className="text-lg lg:text-xl text-gray-600">
                  {language === 'tr' ? 'Alanında uzman mühendis ve teknisyenlerden oluşan deneyimli ekibimiz' : t('about.team.subtitle')}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                <div className="text-center group">
                  <div className="relative mb-6 lg:mb-8">
                    <div className="bg-white/60 backdrop-blur-sm p-4 sm:p-6 rounded-3xl shadow-xl border border-white/20 group-hover:shadow-2xl transition-all duration-300">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Professional%20agricultural%20engineer%20in%20field%20wearing%20safety%20equipment%2C%20experienced%20irrigation%20system%20specialist%2C%20middle-aged%20professional%20with%20technical%20expertise%2C%20outdoor%20agricultural%20setting%20with%20irrigation%20equipment&width=300&height=300&seq=team-1&orientation=squarish"
                        alt="Mühendis"
                        className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 object-cover object-top rounded-2xl mx-auto shadow-lg"
                      />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 lg:mb-3 text-gray-900">
                    {language === 'tr' ? 'Mühendislik Ekibi' : t('about.team.engineering.title')}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    {language === 'tr' ? 'Sulama sistemleri tasarım ve projelendirme uzmanları' : t('about.team.engineering.desc')}
                  </p>
                </div>

                <div className="text-center group">
                  <div className="relative mb-6 lg:mb-8">
                    <div className="bg-white/60 backdrop-blur-sm p-4 sm:p-6 rounded-3xl shadow-xl border border-white/20 group-hover:shadow-2xl transition-all duration-300">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Skilled%20irrigation%20technician%20installing%20drip%20irrigation%20system%2C%20professional%20worker%20with%20technical%20tools%2C%20experienced%20field%20technician%20in%20agricultural%20setting%2C%20hands-on%20irrigation%20system%20installation&width=300&height=300&seq=team-2&orientation=squarish"
                        alt="Teknisyen"
                        className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 object-cover object-top rounded-2xl mx-auto shadow-lg"
                      />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 lg:mb-3 text-gray-900">
                    {language === 'tr' ? 'Teknik Ekip' : t('about.team.technical.title')}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    {language === 'tr' ? 'Sistem kurulum ve bakım uzmanları' : t('about.team.technical.desc')}
                  </p>
                </div>

                <div className="text-center group sm:col-span-2 lg:col-span-1">
                  <div className="relative mb-6 lg:mb-8">
                    <div className="bg-white/60 backdrop-blur-sm p-4 sm:p-6 rounded-3xl shadow-xl border border-white/20 group-hover:shadow-2xl transition-all duration-300">
                      <img 
                        src="https://readdy.ai/api/search-image?query=Agricultural%20consultant%20providing%20customer%20service%2C%20professional%20advisor%20explaining%20irrigation%20solutions%2C%20customer%20support%20specialist%20in%20modern%20office%20environment%2C%20consultation%20meeting%20for%20farming%20projects&width=300&height=300&seq=team-3&orientation=squarish"
                        alt="Danışman"
                        className="w-40 h-40 sm:w-48 sm:h-48 lg:w-56 lg:h-56 object-cover object-top rounded-2xl mx-auto shadow-lg"
                      />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 lg:mb-3 text-gray-900">
                    {language === 'tr' ? 'Danışmanlık Ekibi' : t('about.team.consulting.title')}
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base lg:text-lg">
                    {language === 'tr' ? 'Müşteri ilişkileri ve proje danışmanlığı uzmanları' : t('about.team.consulting.desc')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </main>
    </div>
  );
}